// LEGAL TEMPLATE NOTICE: This is a business / legal template and
// must be reviewed by qualified counsel before production. Update
// the bracketed placeholders (legal entity name, registered office,
// jurisdiction) with real, current values before shipping. Anything
// stated here is descriptive, not legal advice.

import { LegalSection, LegalList } from '../../components/legal/LegalSection';

export default function PrivacyPolicyPage() {
  return (
    <article>
      <header className="mb-2">
        <h1 className="text-3xl sm:text-4xl font-bold text-ink-900 m-0 leading-tight">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-ink-500 m-0">
          Last updated: May 2026
        </p>
      </header>

      <p className="mt-5 text-sm sm:text-[15px] text-ink-700 leading-relaxed">
        Glass-i is operated by{' '}
        <strong>Glass-i [Legal Entity Name to be updated]</strong>{' '}
        (&ldquo;Glass-i&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;).
        We respect your privacy and are committed to protecting personal data
        we process in connection with our website, signup flow, SaaS
        application, support channels, and payment flows.
      </p>

      <LegalSection title="1. Introduction">
        <p>
          Glass-i provides SaaS tools for glass processing businesses. This
          Privacy Policy explains how Glass-i collects, uses, stores, shares,
          and protects personal data when users access the website, signup
          flow, SaaS application, support channels, and payment flows.
        </p>
      </LegalSection>

      <LegalSection title="2. Data we collect">
        <p>We collect the following categories of data:</p>
        <LegalList
          items={[
            'Account and signup details: contact name, email, mobile number, GSTIN, and company details supplied during signup.',
            'Login / account details: username, login email, role, and profile details for master and sub-user accounts.',
            'Billing and payment details: selected plan, invoice details, payment status, Razorpay payment / order references, and amounts.',
            'Usage and security data: IP address, device and browser information, request logs, session metadata, and audit logs.',
            'Communications and support data: tickets, emails, messages, and other correspondence with our support channels.',
            'GST and compliance data entered into the SaaS by customer users in the course of running their business workflows.'
          ]}
        />
        <p className="text-ink-700">
          <strong>What we do not store:</strong> Glass-i does not store full
          card numbers, CVV, UPI PIN, netbanking passwords, or sensitive
          payment authentication credentials. Payments are processed by
          Razorpay and our banking / payment partners.
        </p>
      </LegalSection>

      <LegalSection title="3. Purpose of processing">
        <LegalList
          items={[
            'Account creation and GSTIN verification.',
            'OTP delivery and account-related communication.',
            'Subscription billing, plan management, and invoice generation.',
            'Payment verification and reconciliation.',
            'Providing the SaaS service and the features applicable to your plan.',
            'Security, abuse prevention, and audit logging.',
            'Customer support and service communication.',
            'Compliance with legal, tax, and regulatory obligations.',
            'Service improvement, troubleshooting, and product analytics.'
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Legal basis / lawful purpose">
        <p>
          We process personal data for lawful purposes, including for the
          performance of the contract with you (when you sign up and use the
          service), for compliance with applicable laws and regulations, for
          our legitimate business and security interests, and on the basis of
          your consent where consent is required by law.
        </p>
      </LegalSection>

      <LegalSection title="5. Razorpay and payment processing">
        <p>
          Payments to Glass-i are processed through Razorpay and our banking /
          payment partners. When you make a payment, you may be redirected to
          or interact with the Razorpay checkout interface, where you provide
          payment details directly to Razorpay.
        </p>
        <p>
          Razorpay processes your payment information under its own terms and
          privacy policy. Glass-i receives only limited confirmation and
          reference details (such as Razorpay payment / order identifiers,
          amount, status, and method category) needed to verify the payment,
          activate your subscription, and generate invoices.
        </p>
      </LegalSection>

      <LegalSection title="6. Data sharing">
        <p>We may share data with:</p>
        <LegalList
          items={[
            'Razorpay, payment gateways, and banking partners for payment processing and reconciliation.',
            'GST, GSP, e-invoice, and e-waybill integration providers, where applicable to your usage.',
            'Cloud hosting and infrastructure providers that host our application and data.',
            'Email, SMS, and OTP delivery providers required for account verification and notifications.',
            'Auditors, legal advisors, regulators, and law-enforcement authorities when required by law or to protect our rights and the safety of our users.',
            'Authorized internal team members on a need-to-know basis under appropriate confidentiality obligations.'
          ]}
        />
        <p>We do not sell personal data.</p>
      </LegalSection>

      <LegalSection title="7. Data retention">
        <LegalList
          items={[
            'Account, billing, and tax records are retained for the periods required under applicable Indian tax, accounting, and corporate law.',
            'Audit and security logs are retained for legitimate business, security, and compliance purposes.',
            'Inactive account data may be retained as required to satisfy legal, regulatory, dispute-resolution, or contractual obligations.',
            'You may request deletion of personal data, subject to our legal retention obligations and to data we are required to keep for ongoing services.'
          ]}
        />
      </LegalSection>

      <LegalSection title="8. Data security">
        <p>
          We use reasonable technical and organizational measures to protect
          personal data, including encryption of data in transit, role-based
          access controls, audit logging, session controls, and routine
          backups. No method of transmission or storage is fully secure, and
          we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection title="9. User rights">
        <p>
          Subject to applicable Indian data-protection law, you may request:
        </p>
        <LegalList
          items={[
            'Access to, correction of, or update of your personal data.',
            'Withdrawal of consent for processing carried out on the basis of consent.',
            'Deletion of personal data, subject to our legal and compliance obligations.',
            'Grievance redressal through the contact details below.'
          ]}
        />
      </LegalSection>

      <LegalSection title="10. Children">
        <p>
          Glass-i is intended for use by businesses and their authorized
          personnel. The service is not directed at children, and we do not
          knowingly collect personal data from minors.
        </p>
      </LegalSection>

      <LegalSection title="11. Cookies and tracking">
        <p>
          We use essential cookies and session storage required to keep you
          signed in, maintain your signup state, and protect against abuse.
          Where we add product analytics in the future, we will limit data
          collection to what is necessary for product improvement and
          security. Today, our use is limited to functionality and security.
        </p>
      </LegalSection>

      <LegalSection title="12. International transfers">
        <p>
          Our cloud providers and processors may operate from facilities
          located in India and / or other jurisdictions. Where data is
          processed outside India, we rely on appropriate safeguards and
          contracts with those processors and comply with applicable law.
        </p>
      </LegalSection>

      <LegalSection title="13. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. The
          &ldquo;Last updated&rdquo; date at the top of this page reflects
          the latest version. Material changes will be notified through the
          service or via email where appropriate.
        </p>
      </LegalSection>

      <LegalSection title="14. Contact and grievance redressal">
        <p>
          For privacy questions, data-rights requests, or grievance redressal,
          please contact:
        </p>
        <div className="rounded-xl border border-surface-200 bg-surface-50 px-4 py-3 text-sm text-ink-700 space-y-1">
          <p className="m-0">
            <strong>Grievance Officer, Glass-i</strong>
          </p>
          <p className="m-0">
            Email:{' '}
            <a
              href="mailto:support@glass-i.com"
              className="text-brand-600 hover:text-brand-700"
            >
              support@glass-i.com
            </a>
          </p>
          <p className="m-0">Phone: +91 98765 43210</p>
          <p className="m-0">
            Hours: Monday to Saturday, 10:00 AM to 6:00 PM IST, excluding
            public holidays
          </p>
          <p className="m-0">
            Address: [Registered office address to be updated]
          </p>
        </div>
      </LegalSection>
    </article>
  );
}
